/**
 * iter-109 — Trusted-sender allowlist for inbound aliases.
 *
 * Lets band admins pre-approve email addresses that are NOT registered
 * members but should still be allowed to send to:
 *   - <bandslug>@vivomusicianmanagement.co.uk         (band-wide)
 *   - committee.<bandslug>@vivomusicianmanagement.co.uk (committee)
 *
 * Use cases:
 *   - The chair's personal Gmail isn't on their player profile.
 *   - External venue / promoter contacts need to email the committee.
 *
 * The same allowlist governs BOTH aliases — one list, two doors.
 */
import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";

const EmailAllowlistCard = ({ isAdmin }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emails, setEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingFor, setRemovingFor] = useState(null);

  const refresh = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get("/band/email-allowlist");
      setEmails(Array.isArray(data?.allowlist) ? data.allowlist : []);
    } catch (e) {
      // Non-fatal on first load — surface only when the user expanded the card.
      if (open) toast.error("Couldn't load the allowlist");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, open]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!isAdmin) return null;

  const handleAdd = async (e) => {
    e?.preventDefault?.();
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSaving(true);
    try {
      const { data } = await apiClient.post("/band/email-allowlist", { email });
      setEmails(data.allowlist || []);
      setNewEmail("");
      toast.success(`${email} can now send to your band & committee aliases`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add that email");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (email) => {
    setRemovingFor(email);
    try {
      const { data } = await apiClient.delete(
        `/band/email-allowlist?email=${encodeURIComponent(email)}`,
      );
      setEmails(data.allowlist || []);
      toast.success(`Removed ${email}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't remove that email");
    } finally {
      setRemovingFor(null);
    }
  };

  return (
    <Card
      className="border-emerald-500/20"
      data-testid="email-allowlist-card"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left"
        data-testid="email-allowlist-toggle"
      >
        <CardHeader className="cursor-pointer">
          <CardTitle className="flex items-center justify-between gap-3 text-base">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-400" />
              Trusted sender allowlist
              {emails.length > 0 && (
                <Badge variant="secondary" className="ml-1" data-testid="email-allowlist-count">
                  {emails.length}
                </Badge>
              )}
            </span>
            {open ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </CardTitle>
        </CardHeader>
      </button>

      {open && (
        <CardContent className="space-y-4 pt-0">
          <p className="text-sm text-muted-foreground">
            Emails on this list are allowed to send to{" "}
            <strong>your band&apos;s</strong>{" "}
            <code>bandslug@…</code> and <code>committee.bandslug@…</code> aliases
            even if they aren&apos;t registered players. Use this for chair / secretary
            personal addresses, external venue contacts, or partner organisations.
          </p>

          <form onSubmit={handleAdd} className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1">
              <Label htmlFor="allowlist-email" className="sr-only">
                Email address
              </Label>
              <Input
                id="allowlist-email"
                type="email"
                placeholder="e.g. chair@gmail.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={saving}
                data-testid="email-allowlist-input"
              />
            </div>
            <Button
              type="submit"
              disabled={saving || !newEmail.trim()}
              data-testid="email-allowlist-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" />
              {saving ? "Adding…" : "Add"}
            </Button>
          </form>

          {loading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : emails.length === 0 ? (
            <p
              className="text-sm text-muted-foreground italic"
              data-testid="email-allowlist-empty"
            >
              No trusted senders yet. Only registered band members can email the
              aliases right now.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="email-allowlist-rows">
              {emails.map((em) => (
                <li
                  key={em}
                  className="flex items-center justify-between gap-2 rounded-md border border-border bg-card/40 px-3 py-2"
                  data-testid={`email-allowlist-row-${em}`}
                >
                  <span className="text-sm break-all">{em}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(em)}
                    disabled={removingFor === em}
                    aria-label={`Remove ${em}`}
                    data-testid={`email-allowlist-remove-${em}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default EmailAllowlistCard;
